/*  ========================================================================
    # Section - Vertical Accordion
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import bem from '../../utils/bem';
import { theme } from '../../../../tailwind.config';

// Components
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';

const VerticalAccordion = ({ heading, accordionEntries }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [elementOnHover, setElementOnHover] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0
  });
  const intervalTime = 8000;
  const breakpoints = theme.screens;

  const handleItemClick = (index, e) => {
    if (index === activeIndex) {
      return;
    } else {
      e.preventDefault();
      setActiveIndex(index);
    }
  };

  const increaseActiveIndex = () => {
    if (activeIndex < accordionEntries.length - 1) {
      setActiveIndex(prev => prev + 1);
    } else {
      setActiveIndex(0);
    }
  };

  useEffect(() => {
    let intVal;
    if (inView && !elementOnHover) {
      intVal = setInterval(() => {
        increaseActiveIndex();
      }, intervalTime);
    }

    return () => clearInterval(intVal);
  }, [inView, activeIndex, elementOnHover]);

  return (
    <>
      {heading ? (
        <div
          className={`s-vertical-accordion__heading wysiwyg-block`}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
      ) : null}
      <div
        className={`s-vertical-accordion__wrapper`}
        ref={ref}
        onMouseEnter={() => setElementOnHover(true)}
        onMouseLeave={() => setElementOnHover(false)}
      >
        {accordionEntries.map((entry, index) => (
          <a
            className={bem('s-vertical-accordion', 'item', { active: index === activeIndex })}
            key={index}
            title={entry.link ? entry.link['title'] : undefined}
            href={entry.link ? entry.link['url'] : undefined}
            target={entry.link ? entry.link?.target ?? '_blank' : undefined}
            rel={entry.link ? 'noreferrer' : undefined}
            onClick={e => handleItemClick(index, e)}
            aria-hidden={!entry.link}
            role={entry.link ? 'link' : 'generic'}
          >
            <div className={bem('s-vertical-accordion', 'item-header')}>
              {entry.icon ? (
                <figure className='s-vertical-accordion__item-icon'>
                  <img src={entry['icon']['sizes']['medium']} alt={entry['icon']['alt']} />
                </figure>
              ) : null}
              <h3 className={bem('s-vertical-accordion', 'item-title')}>{entry.title}</h3>
            </div>
            {entry.description ? (
              <div className={bem('s-vertical-accordion', 'item-content')}>
                <p className={bem('s-vertical-accordion', 'item-description')}>
                  {entry.description}
                </p>
              </div>
            ) : null}
            <LazyImage
              figureClass='s-vertical-accordion__item-image'
              defaultUrl={entry.background_image.sizes.medium_large}
              placeholderUrl={entry.background_image.sizes.placeholder}
              focusX={entry.background_image.focus.x}
              focusY={entry.background_image.focus.y}
              alt={entry.background_image.alt}
              credits={entry.credits}
            >
              <LazySource srcSet={entry.background_image.sizes.large} mediaMin={breakpoints.lg} />
              <LazySource
                srcSet={entry.background_image.sizes.medium_large}
                mediaMin={breakpoints.md}
              />
              <LazySource srcSet={entry.background_image.sizes.medium} mediaMin={breakpoints.sm} />
            </LazyImage>
          </a>
        ))}
      </div>
    </>
  );
};

VerticalAccordion.propTypes = {
  heading: PropTypes.string,
  accordionEntries: PropTypes.array
};

export default VerticalAccordion;
